import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography, Progress, Tag, Divider, Tooltip } from 'antd';
import {
  CloudServerOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  MobileOutlined,
  LockOutlined,
  WindowsOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { DataCard, StatCard } from '../common';
import M365NotManaged from '../common/M365NotManaged';
import { cippTenantIdOf, isM365Managed } from '../../utils/customerKey';
import { useTenant } from '../../context/TenantContext';
import {
  useCIPPTenants,
  useCIPPUsers,
  useCIPPLicenses,
  useCIPPSecureScore,
  useCIPPUpsellSummary,
} from '../../hooks';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const SectionTitle = styled(Text)`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
  margin-top: 8px;
`;

const LicenseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LicenseItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const LicenseName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const LicenseCount = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 11px;
`;

const FeatureIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const FeatureIcon = styled.span`
  font-size: 14px;
  color: ${({ $enabled }) => ($enabled ? 'var(--odin-success)' : 'var(--odin-warning)')};
`;

const FeatureText = styled.span`
  font-size: 12px;
  color: ${({ $enabled, theme }) => ($enabled ? theme.colors.text : theme.colors.warning)};
`;

const UpsellTag = styled(Tag).attrs({ color: 'warning' })`
  && {
    font-size: 10px;
    margin-left: 8px;
  }
`;

/**
 * Helper function to parse MCP response content
 */
const parseMCPResponse = (response) => {
  if (!response) return null;

  if (response.content && Array.isArray(response.content)) {
    const textContent = response.content.find(c => c.type === 'text');
    if (textContent?.text) {
      try {
        return JSON.parse(textContent.text);
      } catch (e) {
        console.error('[M365Widget] Failed to parse MCP response:', e);
        return null;
      }
    }
  }

  return response;
};

/**
 * Calculate license utilization percentage
 */
const calculateLicenseUsage = (licenses) => {
  if (!Array.isArray(licenses) || licenses.length === 0) return null;

  let totalEnabled = 0;
  let totalConsumed = 0;

  licenses.forEach(license => {
    const enabled = (license.prepaidUnits?.enabled ?? license.prepaidUnits) || 0;
    const consumed = license.consumedUnits || 0;
    totalEnabled += enabled;
    totalConsumed += consumed;
  });

  if (totalEnabled === 0) return 0;
  return Math.round((totalConsumed / totalEnabled) * 100);
};

/**
 * M365Widget - Microsoft 365 data display widget with sales upsell info
 */
const M365Widget = ({
  data: externalData = {},
  loading: externalLoading = false,
  error: externalError = null,
  onRefresh,
  compact = false,
  useFetch = true,
  tenantId: overrideTenantId = null,
}) => {
  const { selectedTenant } = useTenant();
  const tenantId = overrideTenantId || cippTenantIdOf(selectedTenant);
  // A selected customer without a CIPP id: CSG doesn't manage its M365 (spec 2026-10-19)
  const notManaged = !overrideTenantId && !!selectedTenant && !isM365Managed(selectedTenant);

  // Fetch basic data from CIPP-MCP
  const {
    data: tenantsRaw,
    isLoading: tenantsLoading,
    refetch: refetchTenants,
  } = useCIPPTenants({
    enabled: useFetch && !notManaged,
  });

  const {
    data: usersRaw,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useCIPPUsers(tenantId, {
    enabled: useFetch && !!tenantId,
  });

  const {
    data: licensesRaw,
    isLoading: licensesLoading,
    refetch: refetchLicenses,
  } = useCIPPLicenses(tenantId, {
    enabled: useFetch && !!tenantId,
  });

  const {
    data: secureScoreRaw,
    isLoading: scoreLoading,
    error: scoreError,
    refetch: refetchSecureScore,
  } = useCIPPSecureScore(tenantId, {
    enabled: useFetch && !!tenantId,
  });

  // Fetch upsell summary for sales data
  const {
    data: upsellRaw,
    isLoading: upsellLoading,
    refetch: refetchUpsell,
  } = useCIPPUpsellSummary(tenantId, {
    enabled: useFetch && !!tenantId,
  });

  // Parse MCP responses
  const tenantsParsed = useMemo(() => parseMCPResponse(tenantsRaw), [tenantsRaw]);
  const usersParsed = useMemo(() => parseMCPResponse(usersRaw), [usersRaw]);
  const licensesParsed = useMemo(() => parseMCPResponse(licensesRaw), [licensesRaw]);
  const secureScoreParsed = useMemo(() => parseMCPResponse(secureScoreRaw), [secureScoreRaw]);
  const upsellParsed = useMemo(() => parseMCPResponse(upsellRaw), [upsellRaw]);

  // Transform fetched data
  const fetchedData = useMemo(() => {
    if (!useFetch) return null;

    const tenants = tenantsParsed?.tenants || tenantsParsed;
    const tenantCount = Array.isArray(tenants) ? tenants.length : null;

    const users = usersParsed?.users || usersParsed;
    const userCount = Array.isArray(users) ? users.length : null;

    const licenses = licensesParsed?.licenses || licensesParsed;
    const licenseUsage = calculateLicenseUsage(licenses);

    let secureScore = null;
    if (secureScoreParsed?.success && secureScoreParsed.score?.percentage !== undefined) {
      // get_secure_score response: { success, score: { current, max, percentage } }
      secureScore = secureScoreParsed.score.percentage;
    } else if (secureScoreParsed) {
      // Legacy fallback paths
      if (secureScoreParsed.summary?.secureScore?.percentage !== undefined) {
        secureScore = Math.round(secureScoreParsed.summary.secureScore.percentage);
      } else if (secureScoreParsed.secureScore?.scorePercentage !== undefined) {
        secureScore = Math.round(secureScoreParsed.secureScore.scorePercentage);
      } else if (secureScoreParsed.currentScore !== undefined) {
        const currentScore = secureScoreParsed.currentScore;
        const maxScore = secureScoreParsed.maxScore || 100;
        secureScore = Math.round((currentScore / maxScore) * 100);
      }
    }

    // Extract upsell data
    const upsell = upsellParsed?.success ? {
      topLicenses: upsellParsed.licenses?.topLicenses || [],
      intune: upsellParsed.intune || { enabled: false, managedDevices: 0 },
      conditionalAccess: upsellParsed.conditionalAccess || { enabled: false, totalPolicies: 0 },
      autopilot: upsellParsed.autopilot || { enabled: false, enrolledDevices: 0 },
    } : null;

    return {
      tenantCount,
      userCount,
      licenseUsage,
      secureScore,
      upsell,
    };
  }, [useFetch, tenantsParsed, usersParsed, licensesParsed, secureScoreParsed, upsellParsed]);

  const widgetData = useFetch ? fetchedData : externalData;
  const isLoading = useFetch
    ? (tenantsLoading || usersLoading || licensesLoading || scoreLoading || upsellLoading)
    : externalLoading;
  const hasError = useFetch ? scoreError : externalError;

  const handleRefresh = async () => {
    if (useFetch) {
      await Promise.all([
        refetchTenants(),
        tenantId && refetchUsers(),
        tenantId && refetchLicenses(),
        tenantId && refetchSecureScore(),
        tenantId && refetchUpsell(),
      ].filter(Boolean));
    }
    onRefresh?.();
  };

  const {
    tenantCount = null,
    userCount = null,
    licenseUsage = null,
    secureScore = null,
    upsell = null,
  } = widgetData || {};

  if (notManaged && !compact) {
    return (
      <DataCard title="Microsoft 365" icon={CloudServerOutlined}>
        <M365NotManaged />
      </DataCard>
    );
  }

  if (compact) {
    return (
      <StatCard
        icon={CloudServerOutlined}
        value={tenantCount}
        label="M365 Tenants"
        loading={isLoading}
        tooltip="Microsoft 365 tenants managed via CIPP"
      />
    );
  }

  return (
    <DataCard
      title="Microsoft 365"
      icon={CloudServerOutlined}
      loading={isLoading}
      error={hasError}
      onRefresh={handleRefresh}
      empty={!userCount && !secureScore && !licenseUsage && !upsell}
    >
      <Row gutter={[16, 8]}>
        {/* Core Metrics */}
        <Col span={24}>
          <MetricRow>
            <MetricLabel>
              <UserOutlined />
              Users
            </MetricLabel>
            <MetricValue>{userCount ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={24}>
          <MetricRow>
            <MetricLabel>
              <SafetyCertificateOutlined />
              Secure Score
            </MetricLabel>
            {secureScore !== null ? (
              <Progress
                percent={secureScore}
                size="small"
                style={{ width: 100 }}
                strokeColor={secureScore >= 70 ? 'var(--odin-success)' : secureScore >= 50 ? 'var(--odin-warning)' : 'var(--odin-error)'}
              />
            ) : (
              <MetricValue>--</MetricValue>
            )}
          </MetricRow>
        </Col>
        <Col span={24}>
          <MetricRow>
            <MetricLabel>
              <IdcardOutlined />
              License Utilization
            </MetricLabel>
            {licenseUsage !== null ? (
              <Progress
                percent={licenseUsage}
                size="small"
                style={{ width: 100 }}
                strokeColor="var(--odin-accent)"
              />
            ) : (
              <MetricValue>--</MetricValue>
            )}
          </MetricRow>
        </Col>

        {/* Top Licenses Section */}
        {upsell?.topLicenses && upsell.topLicenses.length > 0 && (
          <Col span={24}>
            <Divider style={{ margin: '8px 0', borderColor: 'var(--odin-border)' }} />
            <SectionTitle>Top Licenses</SectionTitle>
            <LicenseList>
              {upsell.topLicenses.map((license, index) => (
                <Tooltip key={index} title={`${license.assigned} of ${license.total} assigned (${license.utilization}%)`}>
                  <LicenseItem>
                    <LicenseName>{license.name}</LicenseName>
                    <LicenseCount>{license.assigned}/{license.total}</LicenseCount>
                  </LicenseItem>
                </Tooltip>
              ))}
            </LicenseList>
          </Col>
        )}

        {/* Feature Indicators Section */}
        {upsell && (
          <Col span={24}>
            <Divider style={{ margin: '8px 0', borderColor: 'var(--odin-border)' }} />
            <SectionTitle>Features</SectionTitle>

            {/* Intune */}
            <FeatureIndicator>
              <FeatureIcon $enabled={upsell?.intune?.enabled}>
                {upsell?.intune?.enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              </FeatureIcon>
              <MobileOutlined style={{ color: upsell?.intune?.enabled ? 'var(--odin-accent)' : 'var(--odin-warning)' }} />
              <FeatureText $enabled={upsell?.intune?.enabled}>
                Intune {upsell?.intune?.enabled ? `(${upsell?.intune?.managedDevices || 0} devices)` : ''}
              </FeatureText>
              {!upsell?.intune?.enabled && <UpsellTag>Upsell</UpsellTag>}
            </FeatureIndicator>

            {/* Conditional Access */}
            <FeatureIndicator>
              <FeatureIcon $enabled={upsell?.conditionalAccess?.enabled}>
                {upsell?.conditionalAccess?.enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              </FeatureIcon>
              <LockOutlined style={{ color: upsell?.conditionalAccess?.enabled ? 'var(--odin-accent)' : 'var(--odin-warning)' }} />
              <FeatureText $enabled={upsell?.conditionalAccess?.enabled}>
                Conditional Access {upsell?.conditionalAccess?.enabled ? `(${upsell?.conditionalAccess?.enabledPolicies || 0} policies)` : ''}
              </FeatureText>
              {!upsell?.conditionalAccess?.enabled && <UpsellTag>Upsell</UpsellTag>}
            </FeatureIndicator>

            {/* Autopilot */}
            <FeatureIndicator>
              <FeatureIcon $enabled={upsell?.autopilot?.enabled}>
                {upsell?.autopilot?.enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              </FeatureIcon>
              <WindowsOutlined style={{ color: upsell?.autopilot?.enabled ? 'var(--odin-accent)' : 'var(--odin-warning)' }} />
              <FeatureText $enabled={upsell?.autopilot?.enabled}>
                Autopilot {upsell?.autopilot?.enabled ? `(${upsell?.autopilot?.enrolledDevices || 0} enrolled)` : ''}
              </FeatureText>
              {!upsell?.autopilot?.enabled && <UpsellTag>Upsell</UpsellTag>}
            </FeatureIndicator>
          </Col>
        )}
      </Row>
    </DataCard>
  );
};

M365Widget.propTypes = {
  data: PropTypes.shape({
    tenantCount: PropTypes.number,
    userCount: PropTypes.number,
    licenseUsage: PropTypes.number,
    secureScore: PropTypes.number,
    upsell: PropTypes.object,
  }),
  loading: PropTypes.bool,
  error: PropTypes.object,
  onRefresh: PropTypes.func,
  compact: PropTypes.bool,
  useFetch: PropTypes.bool,
  tenantId: PropTypes.string,
};

M365Widget.defaultProps = {
  data: {},
  loading: false,
  error: null,
  onRefresh: undefined,
  compact: false,
  useFetch: true,
  tenantId: null,
};

export default M365Widget;
